import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { LayoutDashboard, Users, CreditCard, Zap, ChevronLeft, ChevronRight, LogOut, Lock } from 'lucide-react';

const NAV = [
  { path: '/admin/dashboard', icon: <LayoutDashboard size={18} />, label: 'Dashboard' },
  { path: '/admin/users', icon: <Users size={18} />, label: 'Users' },
  { path: '/admin/payments', icon: <CreditCard size={18} />, label: 'Payments' },
];

export default function AdminLayout({ children }) {
  const { admin, adminLogout } = useAdminAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = () => {
    adminLogout();
    navigate('/admin/login');
  };

  return (
    <div style={styles.shell}>
      {/* Sidebar */}
      <aside style={{ ...styles.sidebar, width: collapsed ? 64 : 220 }}>
        {/* Brand */}
        <div style={styles.sidebarBrand}>
          <span style={styles.brandIcon}><Zap size={22} color="#f59e0b" /></span>
          {!collapsed && <span style={styles.brandText}>Admin Panel</span>}
        </div>

        {/* Collapse button */}
        <button
          onClick={() => setCollapsed(c => !c)}
          style={styles.collapseBtn}
          title={collapsed ? 'Expand' : 'Collapse'}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>

        {/* Nav Links */}
        <nav style={styles.nav}>
          {NAV.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              style={({ isActive }) => ({
                ...styles.navLink,
                background: isActive ? 'rgba(245,158,11,0.15)' : 'transparent',
                color: isActive ? '#f59e0b' : 'rgba(255,255,255,0.6)',
                borderLeft: isActive ? '3px solid #f59e0b' : '3px solid transparent',
              })}
            >
              <span style={styles.navIcon}>{item.icon}</span>
              {!collapsed && <span style={styles.navLabel}>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Admin info + logout */}
        <div style={styles.sidebarFooter}>
          {!collapsed && (
            <div style={styles.adminInfo}>
              <div style={styles.adminAvatar}>{admin?.name?.[0]?.toUpperCase() || 'A'}</div>
              <div>
                <div style={styles.adminName}>{admin?.name || 'Admin'}</div>
                <div style={styles.adminRole}>Super Admin</div>
              </div>
            </div>
          )}
          <button onClick={handleLogout} style={styles.logoutBtn} title="Logout">
            <LogOut size={16} style={{ verticalAlign: 'middle', marginRight: collapsed ? 0 : '8px' }} />
            {!collapsed && 'Logout'}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main style={styles.main}>
        {/* Topbar */}
        <div style={styles.topbar}>
          <div style={styles.topbarBadge}><Lock size={12} style={{ marginRight: '4px', verticalAlign: 'middle' }} /> Admin Mode</div>
          <div style={styles.topbarRight}>
            <span style={styles.topbarAdmin}>Logged in as <strong>{admin?.email}</strong></span>
          </div>
        </div>

        {/* Page Content */}
        <div style={styles.content}>
          {children}
        </div>
      </main>
    </div>
  );
}

const styles = {
  shell: {
    display: 'flex',
    minHeight: '100vh',
    background: '#0f0f1a',
    fontFamily: "'Inter', sans-serif",
    color: '#fff',
  },
  sidebar: {
    background: 'linear-gradient(180deg, #13131f 0%, #1a1a2e 100%)',
    borderRight: '1px solid rgba(255,255,255,0.07)',
    display: 'flex',
    flexDirection: 'column',
    transition: 'width 0.2s ease',
    overflow: 'hidden',
    flexShrink: 0,
    position: 'sticky',
    top: 0,
    height: '100vh',
  },
  sidebarBrand: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '20px 16px 12px',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
  },
  brandIcon: { fontSize: '22px' },
  brandText: {
    fontSize: '15px',
    fontWeight: '700',
    color: '#f59e0b',
    whiteSpace: 'nowrap',
  },
  collapseBtn: {
    background: 'none',
    border: 'none',
    color: 'rgba(255,255,255,0.3)',
    cursor: 'pointer',
    padding: '8px 16px',
    textAlign: 'left',
    fontSize: '12px',
    width: '100%',
  },
  nav: {
    flex: 1,
    padding: '8px 0',
    overflowY: 'auto',
  },
  navLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    textDecoration: 'none',
    transition: 'all 0.15s',
    fontSize: '14px',
    fontWeight: '500',
    whiteSpace: 'nowrap',
  },
  navIcon: { fontSize: '18px', flexShrink: 0 },
  navLabel: {},
  sidebarFooter: {
    borderTop: '1px solid rgba(255,255,255,0.06)',
    padding: '16px',
  },
  adminInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '12px',
  },
  adminAvatar: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #f59e0b, #ef4444)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '700',
    fontSize: '14px',
    flexShrink: 0,
  },
  adminName: { fontSize: '13px', fontWeight: '600', color: '#fff' },
  adminRole: { fontSize: '11px', color: '#f59e0b', fontWeight: '500' },
  logoutBtn: {
    width: '100%',
    background: 'rgba(239,68,68,0.1)',
    border: '1px solid rgba(239,68,68,0.2)',
    color: '#fca5a5',
    padding: '9px 12px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    textAlign: 'left',
    transition: 'background 0.2s',
  },
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  topbar: {
    background: 'rgba(255,255,255,0.02)',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    padding: '12px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  topbarBadge: {
    fontSize: '12px',
    background: 'rgba(245,158,11,0.1)',
    border: '1px solid rgba(245,158,11,0.2)',
    color: '#f59e0b',
    padding: '4px 12px',
    borderRadius: '100px',
    fontWeight: '600',
  },
  topbarRight: {},
  topbarAdmin: { fontSize: '13px', color: 'rgba(255,255,255,0.4)' },
  content: {
    flex: 1,
    padding: '24px',
    overflowY: 'auto',
  },
};
